import React, { useMemo } from 'react';
import { ExternalLink, Loader2, X } from 'lucide-react';
import { getDiagramNameForDisplay, getDiagramTypeDisplayLabel } from '@/dashboard/utils/diagramHelpers';
import { useDiagramInfo } from '@/hooks/useDiagramInfo';
import { useNavigationStore } from '@/stores/navigationStore';
import { useLinkPreviewStore } from '@/stores/linkPreviewStore';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useI18nLocale, useTranslation } from '@/hooks/useTranslation';

export const DiagramLinkPreviewModal: React.FC = () => {
  const { t: tDiagram } = useTranslation('diagram');
  const { t: tDashboard } = useTranslation('dashboard');
  const t = tDiagram;
  const i18nLocale = useI18nLocale();

  // Important: selectors must return referentially-stable values; returning a new object here
  // can trigger React's "maximum update depth exceeded" via useSyncExternalStore.
  const isOpen = useLinkPreviewStore((s) => s.isOpen);
  const diagramId = useLinkPreviewStore((s) => s.diagramId);
  const close = useLinkPreviewStore((s) => s.close);

  const { data: diagramInfo, isLoading, error } = useDiagramInfo(diagramId ?? undefined);

  const diagramTypeLabel = useMemo(
    () =>
      diagramInfo?.type
        ? getDiagramTypeDisplayLabel(tDashboard, tDiagram, diagramInfo.type)
        : null,
    [diagramInfo?.type, tDashboard, tDiagram]
  );

  const diagramDisplayName = useMemo(() => {
    const raw = diagramInfo?.name != null ? String(diagramInfo.name).trim() : '';
    if (!raw) return null;
    return getDiagramNameForDisplay(raw, tDiagram, tDashboard);
  }, [diagramInfo?.name, tDiagram, tDashboard]);

  const currentDiagramId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const globalObj = window as unknown as { __DIAGRAM_ID__?: string };
    if (globalObj.__DIAGRAM_ID__) return globalObj.__DIAGRAM_ID__;
    const match = window.location.pathname.match(/\/f\/([^/]+)\/d\/([^/]+)/);
    return match ? match[2] : null;
  }, []);

  const currentFolderIdFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return null;
    const match = window.location.pathname.match(/\/f\/([^/]+)\/d\/([^/]+)/);
    return match ? match[1] : null;
  }, []);

  const { data: currentDiagramInfo } = useDiagramInfo(currentDiagramId ?? undefined);

  const iframeSrc = useMemo(() => {
    if (!diagramId || !diagramInfo?.folderId) return null;
    const qs = new URLSearchParams({ embed: '1', readonly: '1' });
    const lng = (i18nLocale || 'en').toLowerCase();
    qs.set('lng', lng);
    return `/f/${diagramInfo.folderId}/d/${diagramId}?${qs.toString()}`;
  }, [diagramId, diagramInfo?.folderId, i18nLocale]);

  const handleOpenInEditor = () => {
    if (!diagramId || !diagramInfo?.folderId) return;

    const { breadcrumbStack } = useNavigationStore.getState();
    const sourceDiagramId = currentDiagramId;
    const sourceNameRaw = currentDiagramInfo?.name != null ? String(currentDiagramInfo.name).trim() : '';
    const sourceDiagramName = sourceNameRaw
      ? getDiagramNameForDisplay(sourceNameRaw, tDiagram, tDashboard)
      : (t('diagramLinkPreviewModal.currentDiagramFallback', { defaultValue: 'Current Diagram' }) as string);
    const sourceFolderId = currentDiagramInfo?.folderId || currentFolderIdFromUrl;

    if (sourceDiagramId && sourceFolderId) {
      const nextStack = [
        ...breadcrumbStack,
        { diagramId: sourceDiagramId, diagramName: sourceDiagramName, folderId: sourceFolderId },
      ];
      try {
        sessionStorage.setItem('diagram-nav-stack', JSON.stringify({ targetDiagramId: diagramId, breadcrumbStack: nextStack }));
        sessionStorage.removeItem('diagram-nav-last'); // legacy
      } catch {}
    }

    close();
    window.location.href = `/f/${diagramInfo.folderId}/d/${diagramId}`;
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      {/* Override DialogContent's default `sm:max-w-lg` so the preview can be wide on desktop. */}
      <DialogContent
        showCloseButton={false}
        className="p-0 w-[90vw] !max-w-[1200px] sm:!max-w-[1200px] h-[86vh] overflow-hidden"
      >
        <div className="h-full w-full flex flex-col">
          <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">
                {diagramDisplayName
                  ? diagramDisplayName
                  : isLoading
                    ? t('diagramLinkPreviewModal.loadingTitle', { defaultValue: 'Loading...' })
                    : t('diagramLinkPreviewModal.untitledDiagram', { defaultValue: 'Untitled Diagram' })}
              </div>
              {diagramTypeLabel ? (
                <div className="text-xs text-muted-foreground truncate">{diagramTypeLabel}</div>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenInEditor}
                disabled={!diagramId || !diagramInfo?.folderId}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('diagramLinkPreviewModal.openInEditor', { defaultValue: 'Open in editor' })}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={close}
                aria-label={t('diagramLinkPreviewModal.closeAriaLabel', { defaultValue: 'Close preview' }) as string}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 bg-muted">
            {isLoading ? (
              <div className="h-full w-full flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : error || !iframeSrc ? (
              <div className="h-full w-full flex items-center justify-center px-6">
                <div className="text-sm text-muted-foreground">
                  {t('diagramLinkPreviewModal.loadFailed', { defaultValue: 'Failed to load preview.' })}
                </div>
              </div>
            ) : (
              <iframe
                title={
                  diagramDisplayName
                    ? (t('diagramLinkPreviewModal.iframeTitleWithName', {
                        name: diagramDisplayName,
                        defaultValue: 'Preview: {{name}}',
                      }) as string)
                    : (t('diagramLinkPreviewModal.iframeTitle', { defaultValue: 'Diagram preview' }) as string)
                }
                src={iframeSrc}
                className="h-full w-full border-0 bg-background"
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
